// src/features/invoice/hooks/useInvoice.js
import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  fetchInvoices,
  fetchInvoiceById,
  fetchInvoiceStats,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  updateInvoiceStatus,
  sendInvoice,
  recordPayment,
  setFilters,
  clearFilters,
  setSort,
  clearError,
  clearSuccess,
} from "../../../app/slice/invoiceSlice";

// Memoized selectors
const selectInvoiceState = (state) => state.invoices || {};
const selectInvoices = (state) => selectInvoiceState(state).invoices || [];
const selectSelectedInvoice = (state) =>
  selectInvoiceState(state).selectedInvoice || null;
const selectLoading = (state) => selectInvoiceState(state).loading || false;
const selectError = (state) => selectInvoiceState(state).error || null;
const selectSuccess = (state) => selectInvoiceState(state).success || null;
const selectFilters = (state) =>
  selectInvoiceState(state).filters || {
    status: "all",
    search: "",
    dateRange: { start: null, end: null },
    clientId: "all",
    minAmount: null,
    maxAmount: null,
    paymentMethod: "all",
  };
const selectStats = (state) =>
  selectInvoiceState(state).stats || {
    total: 0,
    draft: 0,
    sent: 0,
    viewed: 0,
    paid: 0,
    partially_paid: 0,
    overdue: 0,
    cancelled: 0,
    refunded: 0,
    totalValue: 0,
    totalPaid: 0,
    totalOutstanding: 0,
    averageValue: 0,
    collectionRate: 0,
    overdueCount: 0,
  };

export const useInvoice = () => {
  const dispatch = useDispatch();

  const invoices = useSelector(selectInvoices);
  const selectedInvoice = useSelector(selectSelectedInvoice);
  const loading = useSelector(selectLoading);
  const error = useSelector(selectError);
  const success = useSelector(selectSuccess);
  const filters = useSelector(selectFilters);
  const stats = useSelector(selectStats);

  const getInvoices = useCallback(
    (filterParams = {}) => {
      dispatch(fetchInvoices({ ...filters, ...filterParams }));
    },
    [dispatch, filters],
  );

  const getInvoiceById = useCallback(
    (id) => {
      dispatch(fetchInvoiceById(id));
    },
    [dispatch],
  );

  const getStats = useCallback(() => {
    dispatch(fetchInvoiceStats());
  }, [dispatch]);

  const createNewInvoice = useCallback(
    async (data) => {
      const result = await dispatch(createInvoice(data));
      return result;
    },
    [dispatch],
  );

  const updateExistingInvoice = useCallback(
    async (id, data) => {
      const result = await dispatch(updateInvoice({ id, data }));
      return result;
    },
    [dispatch],
  );

  const deleteExistingInvoice = useCallback(
    async (id) => {
      const result = await dispatch(deleteInvoice(id));
      return result;
    },
    [dispatch],
  );

  const updateStatus = useCallback(
    async (id, status) => {
      const result = await dispatch(updateInvoiceStatus({ id, status }));
      return result;
    },
    [dispatch],
  );

  const sendInvoiceToClient = useCallback(
    async (id) => {
      const result = await dispatch(sendInvoice(id));
      return result;
    },
    [dispatch],
  );

  const recordPaymentForInvoice = useCallback(
    async (id, paymentData) => {
      const result = await dispatch(recordPayment({ id, paymentData }));
      return result;
    },
    [dispatch],
  );

  const updateFilters = useCallback(
    (newFilters) => {
      dispatch(setFilters(newFilters));
    },
    [dispatch],
  );

  const clearAllFilters = useCallback(() => {
    dispatch(clearFilters());
  }, [dispatch]);

  const clearErrorMessage = useCallback(() => {
    dispatch(clearError());
  }, [dispatch]);

  const clearSuccessMessage = useCallback(() => {
    dispatch(clearSuccess());
  }, [dispatch]);

  // Auto-fetch on mount
  useEffect(() => {
    getInvoices();
    getStats();
  }, []);

  // Refetch when filters change
  useEffect(() => {
    getInvoices();
  }, [filters]);

  return {
    invoices,
    selectedInvoice,
    loading,
    error,
    success,
    filters,
    stats,
    getInvoices,
    getInvoiceById,
    getStats,
    createNewInvoice,
    updateExistingInvoice,
    deleteExistingInvoice,
    updateStatus,
    sendInvoiceToClient,
    recordPaymentForInvoice,
    updateFilters,
    clearAllFilters,
    clearErrorMessage,
    clearSuccessMessage,
  };
};

export default useInvoice;
